import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Button,
  Grid,
  CircularProgress,
  Typography,
  Box,
  InputAdornment,
  Divider,
} from '@mui/material';
import { updateIndividualProfile } from '../../services/ClientService';

/**
 * Formulário para atualização de perfil de cliente Individual (PF)
 */
export default function IndividualProfileForm({ profileData, onSuccess, onError }) {
  // Estado do formulário
  const [formData, setFormData] = useState({
    email: '',
    login_name: '',
    cpf: '',
    full_name: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
  });

  // Estado de validação do formulário
  const [errors, setErrors] = useState({});

  // Estado de loading
  const [loading, setLoading] = useState(false);

  // Inicializar o formulário com os dados do perfil
  useEffect(() => {
    if (profileData) {
      const initialFormData = {
        email: profileData.email || '',
        login_name: profileData.login_name || '',
        cpf: profileData.cpf || '',
        full_name: profileData.full_name || '',
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        phone_number: profileData.phone_number || '',
        address: profileData.address || '',
        city: profileData.city || '',
        state: profileData.state || '',
        zip_code: profileData.zip_code || '',
      };
      setFormData(initialFormData);
    }
  }, [profileData]);

  // Manipular mudanças no formulário
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Formatação específica para CPF (apenas números)
    if (name === 'cpf') {
      const cpfDigits = value.replace(/\D/g, '');
      setFormData({ ...formData, cpf: cpfDigits.substring(0, 11) });
      return;
    }

    // Formatação específica para CEP (apenas números)
    if (name === 'zip_code') {
      const zipDigits = value.replace(/\D/g, '');
      setFormData({ ...formData, zip_code: zipDigits.substring(0, 8) });
      return;
    }

    // Formatação para telefone (apenas números)
    if (name === 'phone_number') {
      const phoneDigits = value.replace(/\D/g, '');
      setFormData({ ...formData, phone_number: phoneDigits });
      return;
    }

    // Para outros campos, atualiza normalmente
    setFormData({ ...formData, [name]: value });
  };

  // Função de validação
  const validateForm = () => {
    const newErrors = {};

    // Validar email
    if (!formData.email) {
      newErrors.email = 'O email é obrigatório';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    // Validar login_name
    if (!formData.login_name) {
      newErrors.login_name = 'O nome de login é obrigatório';
    } else if (formData.login_name.length < 3) {
      newErrors.login_name = 'O nome de login deve ter pelo menos 3 caracteres';
    }

    // Validar CPF
    if (!formData.cpf) {
      newErrors.cpf = 'O CPF é obrigatório';
    } else if (formData.cpf.length !== 11) {
      newErrors.cpf = 'CPF deve ter 11 dígitos';
    }

    // Validar nome completo
    if (!formData.full_name) {
      newErrors.full_name = 'O nome completo é obrigatório';
    } else if (formData.full_name.length < 3) {
      newErrors.full_name = 'O nome completo deve ter pelo menos 3 caracteres';
    }

    // Validar primeiro nome
    if (!formData.first_name) {
      newErrors.first_name = 'O primeiro nome é obrigatório';
    } else if (formData.first_name.length < 2) {
      newErrors.first_name = 'O primeiro nome deve ter pelo menos 2 caracteres';
    }

    // Validar sobrenome
    if (!formData.last_name) {
      newErrors.last_name = 'O sobrenome é obrigatório';
    } else if (formData.last_name.length < 2) {
      newErrors.last_name = 'O sobrenome deve ter pelo menos 2 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Formatar CPF para exibição
  const formatCPF = (cpf) => {
    if (!cpf) return '';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  // Formatar CEP para exibição
  const formatZipCode = (zipCode) => {
    if (!zipCode) return '';
    return zipCode.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  // Formatar telefone para exibição
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    if (phone.length <= 10) {
      // Formato (XX) XXXX-XXXX para telefones fixos
      return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    // Formato (XX) XXXXX-XXXX para celulares
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar formulário
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Obter o ID do cliente do perfil atual
      const clientId = profileData.id;

      // Enviar dados para atualizar o perfil
      const _result = await updateIndividualProfile(clientId, formData);

      // Notificar sucesso
      onSuccess('Perfil atualizado com sucesso!');
    } catch (error) {
      // Notificar erro
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Dados Pessoais
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.email}
            helperText={errors.email || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Nome de Login"
            name="login_name"
            value={formData.login_name}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.login_name}
            helperText={errors.login_name || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="CPF"
            name="cpf"
            value={formData.cpf}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.cpf}
            helperText={errors.cpf || ''}
            placeholder="Apenas números"
            inputProps={{ inputMode: 'numeric', maxLength: 11 }}
            InputProps={{
              endAdornment: formData.cpf && (
                <InputAdornment position="end">
                  <Typography variant="body2" color="textSecondary">
                    {formatCPF(formData.cpf)}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Nome Completo"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.full_name}
            helperText={errors.full_name || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Primeiro Nome"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.first_name}
            helperText={errors.first_name || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Sobrenome"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.last_name}
            helperText={errors.last_name || ''}
            inputProps={{ maxLength: 100 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>
        Informações de Contato
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Telefone"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            fullWidth
            placeholder="Apenas números"
            inputProps={{ inputMode: 'numeric', maxLength: 11 }}
            InputProps={{
              endAdornment: formData.phone_number && (
                <InputAdornment position="end">
                  <Typography variant="body2" color="textSecondary">
                    {formatPhoneNumber(formData.phone_number)}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="CEP"
            name="zip_code"
            value={formData.zip_code}
            onChange={handleChange}
            fullWidth
            placeholder="Apenas números"
            inputProps={{ inputMode: 'numeric', maxLength: 8 }}
            InputProps={{
              endAdornment: formData.zip_code && (
                <InputAdornment position="end">
                  <Typography variant="body2" color="textSecondary">
                    {formatZipCode(formData.zip_code)}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Endereço"
            name="address"
            value={formData.address}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 150 }}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <TextField
            label="Cidade"
            name="city"
            value={formData.city}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Estado"
            name="state"
            value={formData.state}
            onChange={handleChange}
            fullWidth
            inputProps={{ maxLength: 2 }}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={loading}
          sx={{ minWidth: 200 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Atualizar Perfil'}
        </Button>
      </Box>
    </form>
  );
}

IndividualProfileForm.propTypes = {
  profileData: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
